import { BookOpen } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import ConfirmationDialog from '../../components/ConfirmationDialog';
import EmptyState from '../../components/EmptyState';
import InlineErrorDisplay from '../../components/InlineErrorDisplay';
import LoadingSpinner from '../../components/LoadingSpinner';
import type { LearningLesson, LearningSection, LessonPage } from './services/learningTypes';
import ExportDialog from './components/ExportDialog';
import ImportDialog from './components/ImportDialog';
import LessonForm from './components/LessonForm';
import LessonPreviewModal from './components/LessonPreviewModal';
import LessonsPageHeader from './components/LessonsPageHeader';
import SectionCard from './components/SectionCard';
import SectionForm from './components/SectionForm';
import { getAdminLearningService, type CollectionName } from './services/learningService';
import { useLessonClipboard } from './hooks/useLessonClipboard';

export default function ManageLessons() {
    const [selectedCollection, setSelectedCollection] = useState<CollectionName>('learning-sections');
    const service = useMemo(() => getAdminLearningService(selectedCollection), [selectedCollection]);

    const [sections, setSections] = useState<LearningSection[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
    const [showSectionForm, setShowSectionForm] = useState(false);
    const [showLessonForm, setShowLessonForm] = useState(false);
    const [editingSection, setEditingSection] = useState<LearningSection | null>(null);
    const [editingLesson, setEditingLesson] = useState<{ section: LearningSection; lesson: LearningLesson } | null>(null);
    const [selectedSectionForNewLesson, setSelectedSectionForNewLesson] = useState<LearningSection | null>(null);
    const [pastedLessonData, setPastedLessonData] = useState<{ section: LearningSection; lessonData: Partial<LearningLesson>; pages: LessonPage[] } | null>(null);

    const [showExportDialog, setShowExportDialog] = useState(false);
    const [showImportDialog, setShowImportDialog] = useState(false);
    const [exportConfig, setExportConfig] = useState<{
        mode: 'section' | 'lessons';
        section?: LearningSection;
        lessons?: LearningLesson[];
    } | null>(null);

    const [deleteConfirm, setDeleteConfirm] = useState<{
        show: boolean;
        type: 'section' | 'lesson';
        item: { section: LearningSection; lesson?: LearningLesson } | null;
    }>({ show: false, type: 'section', item: null });

    const [previewLessonInfo, setPreviewLessonInfo] = useState<{
        sectionId: string;
        lessonId: string;
        lessonTitle: string;
    } | null>(null);

    const { copiedLessonId, handlePasteLesson, handlePasteTextFormat, handleCopyLesson } =
        useLessonClipboard(service, setError);

    const loadSections = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const sectionsData = await service.getAllSections();
            setSections(sectionsData);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load sections');
        } finally {
            setLoading(false);
        }
    }, [service]);

    useEffect(() => {
        loadSections();
    }, [loadSections]);

    const handleToggleSection = (sectionId: string) => {
        setExpandedSections(prev => {
            const newSet = new Set(prev);
            if (newSet.has(sectionId)) {
                newSet.delete(sectionId);
            } else {
                newSet.add(sectionId);
            }
            return newSet;
        });
    };

    const handleSectionSaved = () => {
        setShowSectionForm(false);
        setEditingSection(null);
        loadSections();
    };

    const handleLessonSaved = () => {
        setShowLessonForm(false);
        setEditingLesson(null);
        setSelectedSectionForNewLesson(null);
        setPastedLessonData(null);
        loadSections();
    };

    const handleDeleteSection = async () => {
        if (!deleteConfirm.item) return;
        try {
            await service.deleteSection(deleteConfirm.item.section.uid);
            setDeleteConfirm({ show: false, type: 'section', item: null });
            loadSections();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to delete section');
        }
    };

    const handleDeleteLesson = async () => {
        if (!deleteConfirm.item || !deleteConfirm.item.lesson) return;
        try {
            await service.deleteLesson(deleteConfirm.item.section.uid, deleteConfirm.item.lesson.uid);
            setDeleteConfirm({ show: false, type: 'lesson', item: null });
            loadSections();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to delete lesson');
        }
    };

    const handleExportSection = (section: LearningSection) => {
        setExportConfig({ mode: 'section', section });
        setShowExportDialog(true);
    };

    const handleExportLessons = (section: LearningSection) => {
        setExportConfig({ mode: 'lessons', section, lessons: section.lessons });
        setShowExportDialog(true);
    };

    const handlePasteLessonToSection = async (section: LearningSection) => {
        const result = await handlePasteLesson(section);
        if (result) {
            setPastedLessonData(result);
            setSelectedSectionForNewLesson(section);
        }
    };

    const handlePasteTextFormatToSection = async (section: LearningSection) => {
        const result = await handlePasteTextFormat(section);
        if (result) {
            setPastedLessonData(result);
            setSelectedSectionForNewLesson(section);
        }
    };

    if (loading) {
        return <LoadingSpinner message="Loading learning content..." />;
    }

    return (
        <div className={`min-h-screen py-8 ${selectedCollection === 'learning-sections' ? 'bg-gray-50' : 'bg-red-300'}`}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <LessonsPageHeader
                    selectedCollection={selectedCollection}
                    onCollectionChange={setSelectedCollection}
                    onImport={() => setShowImportDialog(true)}
                    onNewSection={() => setShowSectionForm(true)}
                />

                {error && (
                    <InlineErrorDisplay message={error} onDismiss={() => setError(null)} />
                )}

                <div className="space-y-4">
                    {sections.length === 0 ? (
                        <div className="bg-white rounded-lg shadow-md p-4 sm:p-8">
                            <EmptyState
                                icon={<BookOpen size={48} className="mx-auto text-gray-400 mb-4" />}
                                title="No learning sections found"
                                description="Create your first learning section to get started."
                                action={{
                                    label: "Create Section",
                                    onClick: () => setShowSectionForm(true),
                                    variant: "primary",
                                }}
                            />
                        </div>
                    ) : (
                        sections.map((section) => (
                            <SectionCard
                                key={section.uid}
                                section={section}
                                isExpanded={expandedSections.has(section.uid)}
                                copiedLessonId={copiedLessonId}
                                onToggle={() => handleToggleSection(section.uid)}
                                onEdit={() => setEditingSection(section)}
                                onDelete={() => setDeleteConfirm({ show: true, type: 'section', item: { section } })}
                                onExportSection={() => handleExportSection(section)}
                                onExportLessons={() => handleExportLessons(section)}
                                onPasteLesson={() => handlePasteLessonToSection(section)}
                                onPasteTextFormat={() => handlePasteTextFormatToSection(section)}
                                onAddLesson={() => setSelectedSectionForNewLesson(section)}
                                onEditLesson={(lesson) => setEditingLesson({ section, lesson })}
                                onDeleteLesson={(lesson) => setDeleteConfirm({ show: true, type: 'lesson', item: { section, lesson } })}
                                onCopyLesson={(lesson) => handleCopyLesson(section, lesson)}
                                onPreviewLesson={(lesson) => setPreviewLessonInfo({
                                    sectionId: section.uid,
                                    lessonId: lesson.uid,
                                    lessonTitle: lesson.title,
                                })}
                            />
                        ))
                    )}
                </div>
            </div>

            {(showSectionForm || editingSection) && (
                <SectionForm
                    section={editingSection}
                    collection={selectedCollection}
                    service={service}
                    onSave={handleSectionSaved}
                    onCancel={() => {
                        setShowSectionForm(false);
                        setEditingSection(null);
                    }}
                />
            )}

            {(showLessonForm || editingLesson || selectedSectionForNewLesson) && (
                <LessonForm
                    section={editingLesson?.section || selectedSectionForNewLesson!}
                    lesson={editingLesson?.lesson}
                    service={service}
                    onSave={handleLessonSaved}
                    onCancel={() => {
                        setShowLessonForm(false);
                        setEditingLesson(null);
                        setSelectedSectionForNewLesson(null);
                        setPastedLessonData(null);
                    }}
                    initialLessonData={pastedLessonData?.lessonData}
                    initialPages={pastedLessonData?.pages}
                />
            )}

            {showExportDialog && exportConfig && (
                <ExportDialog
                    isOpen={showExportDialog}
                    onClose={() => { setShowExportDialog(false); setExportConfig(null); }}
                    section={exportConfig.section}
                    lessons={exportConfig.lessons}
                    mode={exportConfig.mode}
                />
            )}

            {showImportDialog && (
                <ImportDialog
                    isOpen={showImportDialog}
                    onClose={() => setShowImportDialog(false)}
                    onImportComplete={loadSections}
                    sections={sections}
                />
            )}

            <ConfirmationDialog
                isOpen={deleteConfirm.show}
                title={`Delete ${deleteConfirm.type === 'section' ? 'Section' : 'Lesson'}`}
                message={
                    deleteConfirm.type === 'section'
                        ? `Are you sure you want to delete the section "${deleteConfirm.item?.section.title}"? This will also delete all lessons in this section. This action cannot be undone.`
                        : `Are you sure you want to delete the lesson "${deleteConfirm.item?.lesson?.title}"? This action cannot be undone.`
                }
                confirmText="Delete"
                cancelText="Cancel"
                confirmVariant="danger"
                onConfirm={deleteConfirm.type === 'section' ? handleDeleteSection : handleDeleteLesson}
                onCancel={() => setDeleteConfirm({ show: false, type: 'section', item: null })}
            />

            {previewLessonInfo && (
                <LessonPreviewModal
                    sectionId={previewLessonInfo.sectionId}
                    lessonId={previewLessonInfo.lessonId}
                    lessonTitle={previewLessonInfo.lessonTitle}
                    onClose={() => setPreviewLessonInfo(null)}
                />
            )}
        </div>
    );
}
